import type { Message } from '../../types/message';
import { MediaAttachment, getValidTranscript } from './MediaAttachment';
import { formatFullTooltipDateTime } from '../../utils/formatters';
import { getMemberColor } from '../../utils/colorUtils';
import { activeContactSignal } from '../../state/contacts.store';
import { messagesSignal } from '../../state/messages.store';
import { highlightedMessageIdSignal } from '../../state/ui.store';

function renderFormattedMessageContent(text: string, isUser: boolean, contextId?: string) {
  if (!text) return null;

  const parts = text.split(/(\[\[@[\s\S]+?\]\]|@all|@todos)/gi);

  return parts.map((part, index) => {
    let mentionName = '';
    let isTodos = false;

    if (part.startsWith('[[@') && part.endsWith(']]')) {
      mentionName = part.slice(3, -2).trim();
      if (mentionName.toLowerCase() === 'todos' || mentionName.toLowerCase() === 'all') {
        isTodos = true;
      }
    } else if (part.toLowerCase() === '@all' || part.toLowerCase() === '@todos') {
      isTodos = true;
    }

    if (isTodos) {
      return (
        <span
          key={index}
          class="inline-flex items-center gap-0.5 px-1.5 py-0.5 mx-0.5 rounded-md text-[11px] font-extrabold bg-amber-500/25 text-amber-300 border border-amber-400/40 shadow-xs"
        >
          @Todos
        </span>
      );
    }

    if (mentionName) {
      const color = getMemberColor(mentionName, contextId);
      return (
        <span
          key={index}
          title={`Menção: ${mentionName}`}
          class={`inline-flex items-center gap-0.5 px-1.5 py-0.5 mx-0.5 rounded-md text-[11px] font-bold ${
            isUser
              ? 'bg-cyan-500/30 text-cyan-200 border border-cyan-400/40'
              : `${color.bg} ${color.text} ${color.border} border`
          } shadow-xs transition-all`}
        >
          @{mentionName}
        </span>
      );
    }

    return part;
  });
}

export function MessageBubble({ message, isGroup, showSenderName = true, onOpenVisionModal }: MessageBubbleProps) {
  const activeContact = activeContactSignal.value;
  const contextId = activeContact?.id ? String(activeContact.id) : (message.contact_id ? String(message.contact_id) : undefined);

  const isUser = message.sender_type === 'user';
  const isSystem = message.sender_type === 'system';
  const dateObj = new Date(message.timestamp);
  const timeFormatted = isNaN(dateObj.getTime())
    ? ''
    : dateObj.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const fullTooltip = isNaN(dateObj.getTime()) ? '' : formatFullTooltipDateTime(dateObj);

  let displayContent = message.content || '';
  let displaySenderName = message.sender_name || null;

  const bracketMatch = displayContent.match(/^\[([\s\S]*?)\]:\s*([\s\S]*)$/);
  if (bracketMatch) {
    const extractedSender = bracketMatch[1].trim();
    displayContent = bracketMatch[2].trim();
    if (!displaySenderName || displaySenderName.replace(/\D/g, '') === displaySenderName || displaySenderName.startsWith('+')) {
      if (extractedSender && !extractedSender.startsWith('+') && extractedSender.replace(/\D/g, '') !== extractedSender) {
        displaySenderName = extractedSender;
      }
    }
  }

  const memberColor = !isUser
    ? getMemberColor(displaySenderName || message.sender_id || 'contact', contextId)
    : null;

  if (isSystem) {
    return (
      <div class="flex justify-center my-2">
        <span class="px-3 py-1 bg-slate-900 border border-slate-800 text-[11px] font-mono text-slate-400 rounded-full shadow-sm">
          {message.content}
        </span>
      </div>
    );
  }

  let quotedAuthorName: string | null = null;
  let quotedSnippetText: string | null = null;
  let mainBodyText = displayContent;

  const quoteMatch = displayContent.match(/^>\s*\[\[@([\s\S]+?)\]\]:\s*([\s\S]+?)(?:\n\n|\n(?=[^\n]))([\s\S]*)$/);
  if (quoteMatch) {
    quotedAuthorName = quoteMatch[1].trim();
    quotedSnippetText = quoteMatch[2].trim();
    mainBodyText = quoteMatch[3].trim();
  }

  let displayQuotedSnippet = quotedSnippetText;
  if (quotedAuthorName && quotedSnippetText && /^\[(Áudio|Audio)\]$/i.test(quotedSnippetText.trim())) {
    const authorNameClean = quotedAuthorName.toLowerCase();
    const matchingAudioMsg = (messagesSignal.value || []).find(m => 
      m.message_type === 'audio' &&
      getValidTranscript(m.extracted_text) &&
      (
        (m.sender_name && m.sender_name.toLowerCase().includes(authorNameClean)) ||
        (authorNameClean.includes((m.sender_name || '').toLowerCase()))
      )
    );
    if (matchingAudioMsg?.extracted_text) {
      const transcript = getValidTranscript(matchingAudioMsg.extracted_text);
      if (transcript) {
        displayQuotedSnippet = `🎤 "${transcript}"`;
      }
    }
  }

  const isDocumentMessage = message.message_type === 'document' ||
    Boolean(message.mime_type && message.mime_type.includes('pdf')) ||
    /\.pdf$/i.test(mainBodyText.trim());

  const isMediaPlaceholder = isDocumentMessage || /^\[.*(Figurinha|Imagem|Áudio|Audio|Vídeo|Video|Documento|Sticker|Expirad|Indispon).*\]$/i.test(mainBodyText.trim());
  const textToRender = isMediaPlaceholder ? '' : mainBodyText;

  const groupedReactions = Array.isArray(message.reactions)
    ? message.reactions.reduce((acc, r: any) => {
        const emoji = r?.emoji || r?.reaction_text;
        if (!emoji) return acc;
        if (!acc[emoji]) {
          acc[emoji] = { count: 0, senders: [] };
        }
        acc[emoji].count += 1;
        const sender = r.sender_name || (r.sender_jid ? (r.sender_jid.includes('@') ? r.sender_jid.split('@')[0].split(':')[0] : r.sender_jid) : null);
        if (sender && !acc[emoji].senders.includes(sender)) {
          acc[emoji].senders.push(sender);
        }
        return acc;
      }, {} as Record<string, { count: number; senders: string[] }>)
    : {};

  const reactionEntries = Object.entries(groupedReactions);

  const hasReactions = reactionEntries.length > 0;
  const isHighlighted = highlightedMessageIdSignal.value === message.message_id;

  return (
    <div
      id={`msg-${message.message_id}`}
      data-message-id={message.message_id}
      data-sender-type={message.sender_type}
      data-sender-name={displaySenderName || ''}
      data-timestamp={message.timestamp}
      class={`flex items-end gap-1.5 transition-all duration-300 ${isHighlighted ? 'ring-2 ring-cyan-400 ring-offset-2 ring-offset-slate-950 rounded-2xl p-1 bg-cyan-950/60 shadow-lg shadow-cyan-500/30 animate-pulse' : ''} ${showSenderName ? 'mt-1.5 sm:mt-2 mb-0.5' : 'my-0.5'} ${isUser ? 'justify-end' : 'justify-start'}`}
    >
      <div class={`group relative max-w-[82%] sm:max-w-[80%] md:max-w-xl min-w-[100px] flex flex-col ${isUser ? 'items-end ml-auto' : 'items-start mr-auto'}`}>
        {/* Tooltip on hover */}
        {fullTooltip && (
          <div class="absolute bottom-full mb-1 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-30 px-2.5 py-1 bg-slate-950/95 border border-slate-700 text-[10px] font-mono text-slate-200 rounded-lg shadow-xl whitespace-nowrap">
            {fullTooltip}
          </div>
        )}

        <div
          class={`message-bubble ${isUser ? 'user' : 'contact border'}`}
          style={!isUser && memberColor ? memberColor.bubbleStyle : undefined}
        >
          {isGroup && !isUser && showSenderName && displaySenderName && (
            <span class={`text-[10px] font-bold block mb-0.5 ${memberColor?.text || 'text-cyan-400'}`}>
              {displaySenderName}
            </span>
          )}

          {/* Quoted Message Preview Box (WhatsApp Style) */}
          {quotedAuthorName && displayQuotedSnippet && (
            <div class="mb-1.5 p-2 rounded-lg bg-slate-950/70 border-l-4 border-cyan-400 text-xs shadow-xs backdrop-blur-xs select-none">
              <div class="flex items-center gap-1 text-[11px] font-bold text-cyan-300 mb-0.5">
                <span>{quotedAuthorName}</span>
              </div>
              <p class="text-[11px] text-slate-300 italic line-clamp-2 leading-tight">
                {displayQuotedSnippet}
              </p>
            </div>
          )}

          <MediaAttachment message={message} onOpenVisionModal={onOpenVisionModal} />

          {textToRender && (
            <p class="whitespace-pre-wrap leading-snug break-words relative select-text">
              {renderFormattedMessageContent(textToRender, isUser, contextId)}
              <span class="inline-flex items-center gap-0.5 ml-2.5 float-right select-none opacity-60 text-[9px] font-mono translate-y-[2px] leading-none">
                {timeFormatted}
                {isUser && <span class="text-[9px] text-cyan-400 font-bold ml-0.5">✓</span>}
              </span>
            </p>
          )}

          {!textToRender && (
            <div class="flex items-center justify-end gap-1 pt-0.5">
              <span class="text-[9px] font-mono opacity-60">{timeFormatted}</span>
              {isUser && <span class="text-[9px] text-cyan-400 font-bold">✓</span>}
            </div>
          )}
        </div>

        {/* Reaction Pills in Normal Flow with Negative Top Margin (WhatsApp/Telegram Style with guaranteed spacing) */}
        {hasReactions && (
          <div
            class={`flex flex-wrap items-center gap-1 -mt-2.5 z-20 ${
              isUser ? 'justify-end pr-2.5' : 'justify-start pl-2.5'
            }`}
          >
            {reactionEntries.map(([emoji, data]) => {
              const tooltipText = data.senders.length > 0 ? `${data.senders.join(', ')}: ${emoji}` : emoji;
              return (
                <span
                  key={emoji}
                  title={tooltipText}
                  class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-900/95 border border-slate-700/80 text-slate-100 shadow-[0_4px_12px_rgba(0,0,0,0.5)] backdrop-blur-md select-none hover:scale-110 active:scale-95 transition-all cursor-pointer"
                >
                  <span>{emoji}</span>
                  {data.count > 1 && <span class="text-[9px] font-bold text-cyan-300 ml-0.5">{data.count}</span>}
                </span>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
